<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import Store from '@store/dataProvider';
import { storeToRefs } from 'pinia';
import { IPagination } from '../../types/pagination.type';
import { useToast } from '../../composables/useToast';
import ClientsABMRepository from '@repositories/clientsABM.repository';
import CountriesRepository from '@repositories/countries.repository';
import { IClient } from '../../types/clientABM.type';
import { ICountry } from '../../types/countries.type';
import { useI18n } from "vue-i18n";
import DataTable from "@components/DataTable.vue";

const isLoadingClients = ref<boolean>(false);

const { t } = useI18n();
const store = Store();
const { $toast } = useToast();
const clients = ref<IClient[]>([]);
const currentPage = ref<number>();
const paginationData = ref<IPagination>();
const modalType = ref<string | null>(null);
const modalTitle = ref<string | undefined>();
const isModalOpen = ref<boolean>(false);
const selectedClient = ref<IClient | null>(null);
const countries = ref<ICountry[]>([]);
const isViewActive = ref<boolean>(true);
const searchFormRef = ref<any>(null);
const isLoading = ref<boolean>(false);

const fieldsTableInactive: any = ref([
  {
    key: 'id',
    label: 'Id',
    sortable: true,
  },
  {
    key: 'tradeName',
    label: t('ajustes.clientes.nombre_cliente'),
  },
  {
    key: 'taxCode',
    label: t('ajustes.clientes.rut'),
  },
  {
    key: 'restore',
    label: t('global.restaurar'),
  },
]);

const fieldsTableActive: any = ref([
  {
    key: 'id',
    label: 'Id',
    sortable: true,
  },
  {
    key: 'tradeName',
    label: t('ajustes.clientes.nombre_cliente'),
  },
  {
    key: 'country',
    label: 'Pais',
    formatter: (e: any) => e?.description || null,
  },
  {
    key: 'taxCode',
    label: t('ajustes.clientes.rut'),
  },
  {
    key: 'phone1',
    label: t('ajustes.clientes.telefono'),
  },
  {
    key: 'address',
    label: t('ajustes.clientes.direccion'),
  },
  {
    key: 'clientInfo',
    label: t('global.editar'),
  },
  {
    key: 'delete',
    label: t('global.eliminar'),
  },
]);

const fieldsFilter: any = ref([
  {
    key: 'search',
    title: t('global.buscar'),
    type: 'text',
    placeholder: t('ajustes.clientes.buscar_por_nombre_rfc_telefono'),
    value: null,
    cols: 3,
  },
]);

// API
const getClientsFromAPI = async (
  page: number,
  deleted: boolean = false,
  params = {},
) => {
  isLoadingClients.value = true;
  try {
    const data = await ClientsABMRepository.getClientsProPaginated({
      page,
      deleted,
      ...params,
    });
    if (data && Array.isArray(data?.clients)) {
      paginationData.value = data.pagination;
      updateClients(data.clients);
      return data.clients;
    }
    return [];
  } catch (error) {
    console.error('Error while fetching clients:', error);
    return [];
  } finally {
    isLoadingClients.value = false;
  }
};

const getCountriesFromApi = async () => {
  const data = await CountriesRepository.getcountries({page: 1, blocked: false});
  if (Array.isArray(data?.data?.countries)) {
    countries.value = data.data.countries;
  }
};

const updateClients = (newClients: IClient[]) => {
  clients.value = newClients;
};

const loadClients = async () => {
  await getClientsFromAPI(1);
};

onMounted(() => {
  loadClients();
  getCountriesFromApi();
});

// Create inputs from modals
const fieldsInputClients = computed(() => {
  return [
    {
      key: 'email',
      title: t('ajustes.clientes.email'),
      type: 'text',
      validations: {
        rules: {
          required: true,
        },
      },
      placeholder: t('ajustes.clientes.email'),
      value: selectedClient.value?.managers?.[0]?.email,
      cols: 12,
    },
    {
      key: 'tradeName',
      title: t('ajustes.clientes.nombre_cliente'),
      type: 'text',
      validations: {
        rules: {
          required: true,
        },
      },
      placeholder: t('ajustes.clientes.nombre_cliente'),
      value: selectedClient.value?.tradeName,
      cols: 12,
    },
    {
      key: 'rfc',
      title: t('ajustes.clientes.rut'),
      type: 'text',
      validations: {
        rules: {
          required: true,
        },
      },
      placeholder: t('ajustes.clientes.rut'),
      value: selectedClient.value?.rfc || selectedClient.value?.rut || selectedClient.value?.nif || selectedClient.value?.cuit,
      cols: 12,
    },
    {
      key: 'phone1',
      title: t('ajustes.clientes.telefono'),
      type: 'text',
      placeholder: t('ajustes.clientes.telefono'),
      value: selectedClient.value?.phone1,
      cols: 12,
    },
    {
      key: 'countryId',
      title: t('ajustes.clientes.pais'),
      placeholder: t('ajustes.clientes.pais'),
      label: 'description',
      validations: {
        rules: {
          required: true,
        },
      },
      value: selectedClient.value?.countryId,
      cols: 12,
      searchable: true,
      trackBy: 'id',
      type: 'select',
      formatValue: (val: any) => val?.id ?? null,
      options: countries,
    },
    {
      key: 'address',
      title: t('ajustes.clientes.direccion'),
      type: 'text',
      placeholder: t('ajustes.clientes.direccion'),
      value: selectedClient.value?.address,
      cols: 12,
    },
  ];
});

// UI
const onChangePage = async (page: number) => {
  getClientsFromAPI(page);
  currentPage.value = page;
};

const handleOpenModalCreate = () => {
  modalTitle.value = t('ajustes.clientes.agregar_cliente');
  isModalOpen.value = true;
  modalType.value = 'modalCreateClient';
};

const handleClose = () => {
  modalTitle.value = undefined;
  isModalOpen.value = false;
  modalType.value = null;
  selectedClient.value = null;
};

const handleActive = async () => {
  const clientsData = await getClientsFromAPI(1);
  updateClients(clientsData);
  isViewActive.value = true;
};

const handleInactive = async () => {
  const clientsDeleted = await getClientsFromAPI(1, true);
  updateClients(clientsDeleted);
  isViewActive.value = false;
};

const handleOpenModalDialog = (item: IClient) => {
  modalTitle.value = isViewActive.value
    ? `${t('ajustes.clientes.msg_borrar_cliente')} ${item.id}`
    : `${t('ajustes.clientes.msg_restaurar_cliente')} ${item.id}`;
  modalType.value = 'modalDialog';
  selectedClient.value = item;
  isModalOpen.value = true;
};

// Filter Functions
const handleOnSubmit = async ({ items }: any) => {
  getClientsFromAPI(1, false, items);
};

const handleReset = async () => {
  if (!searchFormRef.value) return;
  await searchFormRef.value.resetValues();
  getClientsFromAPI(1, false);
};

// CRUD
const handleCreateClient = async ({ items, isFormValid }: any) => {
  if (!isFormValid) return;
  try {
    const dataToSend = {
      ...items,
    };
    const data = await ClientsABMRepository.createClient(dataToSend);
    $toast?.success(t('ajustes.clientes.msg_agregar_cliente'));
  } catch (error) {
    $toast?.error(t('ajustes.clientes.msg_error_creacion'));
  }
  getClientsFromAPI(paginationData.value?.current_page ?? 1, !isViewActive.value);
  handleClose();
};

const handleSelectClient = (clientData: IClient) => {
  modalTitle.value = t('ajustes.clientes.actualizar_cliente');
  (isModalOpen.value = true), (modalType.value = 'modalUpdateClient');
  selectedClient.value = clientData;
};

const handleUpdateClient = async ({ items, isFormValid }: any) => {
  if (!isFormValid || !selectedClient.value) return;
  const clientForUpdate = {
    ...items,
    id: selectedClient.value.id,
  };

  try {
    await ClientsABMRepository.updateClient(clientForUpdate);
    $toast?.success(t('ajustes.clientes.msg_actualizado'));
  } catch (error) {
    $toast?.error(t('ajustes.clientes.msg_error_actualizacion'));
  }
  getClientsFromAPI(paginationData.value?.current_page ?? 1, !isViewActive.value);
  handleClose();
};

const handleDeleteRestoreClient = async () => {
  if (selectedClient?.value?.id) {
    let response = null;
    try {
      if (isViewActive.value) {
        response = await ClientsABMRepository.deleteClient(
          selectedClient.value.id,
        );
      } else {
        response = await ClientsABMRepository.restoreClient(
          selectedClient.value.id,
        );
      }
    } catch (error) {
      if (isViewActive.value) {
        return $toast?.error(t('ajustes.clientes.msg_error_eliminar'));
      }
      $toast?.error(t('ajustes.clientes.msg_error_restaurar'));
    }
    getClientsFromAPI(paginationData.value?.current_page ?? 1, !isViewActive.value);
    handleClose();
  }
};
</script>

<template>
  <Layout title="Clientes" :textColor="'color-secondary'">
    <template #layout>
      <div class="d-flex justify-content-between">
        <div>
          <CButton
            class="background-blue button__form"
            style="padding: 0.5rem !important; margin-right: 15px"
            type="button"
            @click="handleActive"
          >
            <template #content>{{ t('global.activos') }}</template>
          </CButton>
          <CButton
            class="background-pink button__form"
            style="padding: 0.5rem !important"
            type="button"
            @click="handleInactive"
          >
            <template #content>{{ t('global.inactivos') }}</template>
          </CButton>
        </div>
        <CButton
          class="background-pink button__form"
          style="padding: 0.5rem !important"
          type="button"
          @click="handleOpenModalCreate"
        >
          <template #content>{{ t('ajustes.clientes.agregar_cliente') }}</template>
        </CButton>
      </div>

      <div class="form__logEmail">
        <Form
          ref="searchFormRef"
          @onSubmit="handleOnSubmit"
          :inputs="fieldsFilter"
          customClass="poliza__form"
        >
          <template #buttons>
            <CButton
              class="background-blue button__form"
              style="padding: 0.6rem !important; margin: 0.6rem"
            >
              <template #content>{{ t('global.buscar') }}</template>
            </CButton>
            <CButton
              class="background-blue button__form"
              style="padding: 0.6rem !important; margin: 0.6rem"
              type="button"
              @click="handleReset"
            >
              <template #content>{{ t('global.limpiar_filtro') }}</template>
            </CButton>
          </template>
        </Form>
      </div>

      <DataTable
        :loading="isLoadingClients"
        :fields="isViewActive ? fieldsTableActive : fieldsTableInactive"
        :items="clients"
        @update:model-value-page="onChangePage"
        :totalRows="paginationData?.count"
        :perPage="20"
        :currentPage="paginationData?.current_page"
      >
        <template v-slot:cell(taxCode)="assetData">
          <span v-if="assetData.data.item?.rfc">RFC: {{ assetData.data.item.rfc }}</span>
          <span v-if="assetData.data.item?.rut">RUT: {{ assetData.data.item.rut }}</span>
          <span v-if="assetData.data.item?.nif">NIF: {{ assetData.data.item.nif }}</span>
          <span v-if="assetData.data.item?.cuit">CUIT: {{ assetData.data.item.cuit }}</span>
        </template>
        <template v-slot:cell(clientInfo)="assetData">
          <div
            @click="handleSelectClient(assetData.data.item)"
            class="button-icon"
          >
            <img src="/icons/editar.svg" />
          </div>
        </template>
        <template v-slot:cell(delete)="operationsData">
          <div
            @click="handleOpenModalDialog(operationsData.data.item)"
            class="button-icon"
          >
            <img src="/icons/basura.svg" alt="eliminar" />
          </div>
        </template>
        <template v-slot:cell(restore)="operationsData">
          <div
            @click="handleOpenModalDialog(operationsData.data.item)"
            class="button-icon restore"
          >
            <img src="/icons/restaurar.svg" alt="restore" />
          </div>
        </template>
      </DataTable>

      <BModal
        :modelValue="isModalOpen"
        hide-footer
        :centered="true"
        @hide="handleClose"
        static
        :title="modalTitle"
      >
        <template v-if="modalType === 'modalCreateClient'">
          <Form
            ref="refTableForm"
            :inputs="fieldsInputClients"
            @onSubmit="handleCreateClient"
          >
            <template #buttons>
              <div class="d-flex">
                <CButton :disabled="isLoading" class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>{{ t('ajustes.clientes.agregar_cliente') }}</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>

        <template v-if="modalType === 'modalUpdateClient'">
          <Form
            ref="refTableForm"
            :inputs="fieldsInputClients"
            @onSubmit="handleUpdateClient"
          >
            <template #buttons>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>{{ t('global.actualizar') }}</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>

        <template v-if="modalType === 'modalDialog'">
          <h5 class="text-center">
            {{
              isViewActive
                ? `¿Está seguro que desea borrar el cliente ${selectedClient?.tradeName}?`
                : `¿Está seguro que desea restaurar el cliente ${selectedClient?.tradeName}?`
            }}
          </h5>
          <div class="d-flex mt-3">
            <CButton
              class="w-100 fs-6 p-2"
              background="background-pink"
              @click="handleDeleteRestoreClient"
            >
              <template #content>
                <div>
                  <span>{{ t('global.confirmar') }}</span>
                </div>
              </template>
            </CButton>
          </div>
        </template>
      </BModal>
    </template>
  </Layout>
</template>
